import { useEffect, useState } from 'react';

import api from '../../utils/api';

import User from '../../models/User';

import type { UserProps } from '../../models/User';

// How long a successful response stays warm before we hit the API again.
// Within this window, additional mounts reuse the cached user instead of
// firing their own request.
const CACHE_TTL_MS = 60_000;

// Two backing endpoints with different payload shapes:
// - 'auth' -> /api/web/v1/auth/getUser, lightweight identity/flags (default)
// - 'full' -> /api/web/v2/users/get, heavier response (customers, etc.)
// Each variant gets its own cache slot so their payloads never clobber one
// another.
type UserVariant = 'auth' | 'full';

type UserResult = { data: UserProps; isError: boolean };

// Subset of the /auth/getUser response we map onto the User model. The endpoint
// names its logged-in flag `isLoggedIn` (vs the model's `loggedIn`), so it can't
// be passed through verbatim.
type AuthUserResponse = Record<string, unknown> & { isLoggedIn?: boolean };

const BASE_URL =
  typeof window !== 'undefined' && location.origin !== 'https://www.zillow.com' ? 'https://www.qa.zillow.net/' : '';

const fetchers: Record<UserVariant, () => Promise<UserProps>> = {
  auth: async () => {
    const data = (await api.auth.getUser({ pageWithSensitiveInformation: false }, BASE_URL)) as AuthUserResponse;
    const { isLoggedIn, ...rest } = data;

    return User({ ...rest, loggedIn: isLoggedIn });
  },
  full: async () => {
    // NOTE: /api/web/v2/users/get handles `includeSummary: true` on BE
    const data = await api.user.info(
      {
        pageWithSensitiveInformation: false,
        includeCustomers: true,
        includeProfileImageUrl: true,
      },
      BASE_URL,
    );

    return User(data as unknown as Record<string, unknown>);
  },
};

type CacheSlot = { result?: UserResult; at: number; inFlight?: Promise<UserResult> };

const caches: Record<UserVariant, CacheSlot> = {
  auth: { at: 0 },
  full: { at: 0 },
};

const fetchUser = async (variant: UserVariant): Promise<UserResult> => {
  try {
    return { data: await fetchers[variant](), isError: false };
  } catch (e) {
    // eslint-disable-next-line
    console.error(`ERROR::useGetUser (${variant}) api failed --`, e);

    return { data: User(), isError: true };
  }
};

// Dedupes concurrent callers onto a single request and serves a cached result
// within the cooldown window. `force` skips the cooldown (but still dedupes
// against any in-flight request). Errors are not cached, so failures can retry.
const getUser = (variant: UserVariant, force = false): Promise<UserResult> => {
  const cache = caches[variant];

  if (!force && cache.result && Date.now() - cache.at < CACHE_TTL_MS) {
    return Promise.resolve(cache.result);
  }

  if (cache.inFlight) {
    return cache.inFlight;
  }

  // Assign synchronously (do not await) so concurrent callers in the same tick
  // see the in-flight promise above and share this single request.
  cache.inFlight = fetchUser(variant).then((result) => {
    cache.inFlight = undefined;

    if (!result.isError) {
      cache.result = result;
      cache.at = Date.now();
    }

    return result;
  });

  return cache.inFlight;
};

const useUser = (variant: UserVariant) => {
  const cache = caches[variant];
  const [data, setData] = useState(cache.result?.data ?? User());
  const [isLoading, setIsLoading] = useState(!cache.result);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let active = true;

    const loadUser = async () => {
      const result = await getUser(variant);

      if (!active) {
        return;
      }

      if (!result.isError) {
        setData(result.data);
      }

      setIsError(result.isError);
      setIsLoading(false);
    };

    void loadUser();

    return () => {
      active = false;
    };
  }, [variant]);

  const refetch = async () => {
    setIsLoading(true);

    const result = await getUser(variant, true);

    if (!result.isError) {
      setData(result.data);
    }

    setIsError(result.isError);
    setIsLoading(false);

    return result;
  };

  const updateUserEmail = ({ email }: { email: string }) => {
    setData((prev) => User({ ...prev, email }));
  };

  return { refetch, data, isLoading, updateUserEmail, isError };
};

// Lightweight default backed by /auth/getUser.
export const useGetUser = () => useUser('auth');

// Heavier variant backed by /users/get for callers that need its richer fields.
export const useGetFullUser = () => useUser('full');
